import { BaseApiService, AppError } from './baseApiService';
import { storage, Entitlements } from '../../utils/storage';
import { environment } from '../../config/environment';
import type { DoctorSpecialty } from '../../utils/specialty';

/**
 * DRF stringifies every value in an APIException detail, so the auth service puts
 * `error_code` on the wire as `"2008"`, not `2008`. Accept either form and fall
 * back to the HTTP status only when the body carries no usable code.
 */
function parseErrorCode(rawCode: unknown, status: number): number {
  if (typeof rawCode === 'number') {
    return Number.isFinite(rawCode) ? rawCode : status;
  }
  if (typeof rawCode === 'string' && rawCode.trim() !== '') {
    const parsed = Number(rawCode);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return status;
}

function throwNormalizedError(response: Response, errorBody: Record<string, unknown>): never {
  throw new AppError(
    (errorBody.message as string) || (errorBody.error as string) || response.statusText,
    response.status,
    parseErrorCode(errorBody.error_code, response.status),
    (errorBody.details as string) || '',
    errorBody
  );
}

/**
 * The backend wraps successful responses from the new registration endpoints in a
 * `{ status, message, data }` envelope (create_success_response). Token/login endpoints
 * return their payload at the top level. This unwraps `data` when present so callers can
 * rely on the documented top-level response shape either way.
 */
function unwrapData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  refresh_token: string;
  user_context?: {
    id: number;
    email: string;
    phone_no: string;
    name: string;
    specialty?: string;
    specialties?: string[];
    is_admin: boolean;
    is_medical_officer: boolean;
    is_patient: boolean;
    clinic_id?: number;
    clinic_name?: string;
  };
  user_info?: {
    name: string;
    email: string;
    phone_no: string;
    age: string;
    gender: string;
    country: string;
    specialty?: string;
    specialties?: string[];
    clinic_id: number;
    is_admin: boolean;
    is_super_admin: boolean;
    is_patient: boolean;
    is_medical_officer: boolean;
  };
  registration_status?: {
    is_verified: number;
    step_1_password: number;
    step_2_mfa: number;
    step_3_clinic: number;
    step_4_subscription: number;
    step_5_medical_officers: number;
    current_step: number | null;
    is_registration_complete: boolean;
    completion_percentage: number;
    user_id: number;
    user_email: string;
    clinic_id?: number;
    clinic_is_approved?: boolean;
    approval_status?: 'pending' | 'approved' | 'rejected';
  };
  requires_registration_completion?: boolean;
}

// New MFA Login Response Types
interface MFARequiredResponse {
  mfa_required: true;
  mfa_tx_id: string;
  message: string;
  expires_in: number;
}

interface MFASetupRequiredResponse {
  error: 'mfa_setup_required';
  error_description: string;
  registration_status: {
    is_verified: number;
    step_1_password: number;
    step_2_mfa: number;
  };
  user_info: {
    user_id: number;
    email: string;
    phone_no: string;
    is_mfa_setup: boolean;
  };
  message: string;
}

// Login API can only return these two responses - all users must go through MFA
type LoginResponse = MFARequiredResponse | MFASetupRequiredResponse;

// MFA Verification Interfaces (Updated)
interface MFAVerifyLoginRequest {
  mfa_tx_id: string;
  otp: string;
}

interface MFAVerifyLoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
  user_context: {
    id: number;
    email: string;
    phone_no: string;
    name: string;
    specialty?: string;
    specialties?: string[];
    is_admin: boolean;
    is_super_admin: boolean;
    is_medical_officer: boolean;
    is_patient: boolean;
    is_approved: boolean;
    clinic_id?: number;
    clinic_name?: string;
    entitlements?: Entitlements;
  };
}

// MFA Setup Interfaces (Updated) 
interface InitiateMFASetupLoginRequest {
  email: string;
  phone_no: string;
}

interface InitiateMFASetupLoginResponse {
  status: string;
  message: string;
  data: {
    message: string;
    phone_number: string;
    user_email: string;
    mfa_session: boolean;
  };
}

interface CompleteMFASetupLoginRequest {
  email: string;
  otp: string;
  client_id: string;
}

interface CompleteMFASetupLoginResponse {
  status: string;
  message: string;
  data: {
    message: string;
    user_email: string;
    mfa_enabled: boolean;
    account_verified: boolean;
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    scope: string;
    user_context: {
      id: number;
      email: string;
      phone_no: string;
      name: string;
      specialty?: string;
    specialties?: string[];
      is_admin: boolean;
      is_super_admin: boolean;
      is_medical_officer: boolean;
      is_patient: boolean;
      clinic_id?: number;
      clinic_name?: string;
      entitlements?: Entitlements;
    };
  };
}

interface LoginRequest {
  username: string; // email
  password: string;
}

interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  phone_no: string;
}

interface RegisterResponse {
  status: string;
  message: string;
  data: {
    id: number;
    name: string;
    chat_id: string;
    email: string;
    age: string;
    gender: string;
    country: string;
    phone_no: string;
    description: string;
    profile_image: string;
    past_medical_conditions: string;
    medical_officer_registration_number: string;
    is_active: number;
    is_verified: number;
    is_admin: number;
    is_patient: number;
    is_medical_officer: number;
    logged_on: string;
    created_on: string;
    updated_on: string;
  };
}

interface UserStatusResponse {
  status: string;
  message: string;
  data: {
    is_verified: number;
    step_1_password: number;
    step_2_mfa: number;
    step_3_clinic: number;
    step_4_subscription: number;
    step_5_medical_officers: number;
    current_step: number | null;
    is_registration_complete: boolean;
    completion_percentage: number;
    user_id: number;
    user_email: string;
    clinic_id?: number;
    clinic_is_approved?: boolean;
    approval_status?: 'pending' | 'approved' | 'rejected';
  };
}

interface EmailVerifyRequest {
  email: string;
  token: string;
}

interface EmailVerifyResponse {
  status: string;
  message: string;
  data: {
    message: string;
    verified: boolean;
    user_email: string;
  };
}

interface SetPasswordRequest {
  email: string;
  token: string;
  new_password: string;
}

interface SetPasswordResponse {
  status: string;
  message: string;
  data: {
    message: string;
    user_email: string;
  };
}

interface SendMFATokenRequest {
  phone_no: string;
  email: string;
  token: string;
}

interface SendMFATokenResponse {
  status: string;
  message: string;
  data: {
    message: string;
    phone_number: string;
    user_email: string;
    mfa_session: boolean;
  };
}

interface CompleteMFARequest {
  otp: string;
  email: string;
  token: string;
  client_id: string;
}

interface CompleteMFAResponse {
  status: string;
  message: string;
  data: {
    message: string;
    user_email: string;
    mfa_enabled: boolean;
    account_verified: boolean;
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    scope: string;
    user_context: {
      id: number;
      email: string;
      phone_no: string;
      name: string;
      is_admin: boolean;
      is_super_admin: boolean;
      is_medical_officer: boolean;
      is_patient: boolean;
    };
  };
}

interface PasswordResetRequest {
  email: string;
}

interface PasswordResetResponse {
  status: string;
  message: string;
  data: {
    message: string;
  };
}

interface PasswordResetConfirmRequest {
  email: string;
  token: string;
  new_password: string;
}

interface PasswordResetConfirmResponse {
  status: string;
  message: string;
  data: {
    message: string;
  };
}

// Password Reset OTP Interfaces
interface SendPasswordResetOTPRequest {
  email: string;
}

interface SendPasswordResetOTPResponse {
  status: string;
  message: string;
  data: {
    message: string;
  };
}

interface VerifyPasswordResetOTPRequest {
  email: string;
  otp: string;
}

interface VerifyPasswordResetOTPResponse {
  status: string;
  message: string;
  data: {
    message: string;
    otp_verified: boolean;
  };
}

// MFA Login Interfaces
interface MFALoginVerifyRequest {
  email: string;
  otp: string;
}

interface MFALoginVerifyResponse {
  status: string;
  message: string;
  data: {
    message: string;
    email: string;
  };
}

interface MFALoginResendRequest {
  email: string;
  /** Current challenge id — lets the backend reset the attempt counter in place. */
  mfa_tx_id?: string;
}

interface MFALoginResendResponse {
  status: string;
  message: string;
  data: {
    message: string;
    email: string;
    /** New/refreshed challenge id the caller must verify against. */
    mfa_tx_id?: string;
  };
}

// Admin Registration Interfaces
interface ClinicSetupRequest {
  clinic_name: string;
  clinic_phone: string;
  clinic_email: string;
  clinic_abn: string;
  clinic_address_line_1: string;
  clinic_address_line_2?: string;
  clinic_city: string;
  clinic_zip_code: string;
  clinic_country: string;
}

interface ClinicSetupResponse {
  status: string;
  message: string;
  data: {
    message: string;
    registration_status: {
      is_verified: number;
      step_1_password: number;
      step_2_mfa: number;
      step_3_clinic: number;
      step_4_subscription: number;
      step_5_medical_officers: number;
      current_step: number | null;
      is_registration_complete: boolean;
      completion_percentage: number;
      user_id: number;
      user_email: string;
      clinic_id?: number;
      clinic_is_approved?: boolean;
      approval_status?: 'pending' | 'approved' | 'rejected';
    };
    next_step: number;
  };
}

interface SubscriptionSetupRequest {
  email: string;
}

interface SubscriptionSetupResponse {
  status: string;
  message: string;
  data: {
    message: string;
    registration_status: {
      is_verified: number;
      step_1_password: number;
      step_2_mfa: number;
      step_3_clinic: number;
      step_4_subscription: number;
      step_5_medical_officers: number;
      current_step: number | null;
      is_registration_complete: boolean;
      completion_percentage: number;
      user_id: number;
      user_email: string;
      clinic_id?: number;
      clinic_is_approved?: boolean;
      approval_status?: 'pending' | 'approved' | 'rejected';
    };
    next_step: number;
  };
}

interface MedicalOfficer {
  name: string;
  email: string;
  phone_no: string;
  medical_officer_registration_number: string;
}

interface MedicalOfficersSetupRequest {
  medical_officers: MedicalOfficer[];
}

interface MedicalOfficersSetupResponse {
  status: string;
  message: string;
  data: {
    message: string;
    clinic_id: number;
    registration_status: {
      is_verified: number;
      step_1_password: number;
      step_2_mfa: number;
      step_3_clinic: number;
      step_4_subscription: number;
      step_5_medical_officers: number;
      current_step: number | null;
      is_registration_complete: boolean;
      completion_percentage: number;
      user_id: number;
      user_email: string;
      clinic_id?: number;
      clinic_is_approved?: boolean;
      approval_status?: 'pending' | 'approved' | 'rejected';
    };
    registration_complete: boolean;
  };
}

// ============================================================================
// Redesigned registration + login flow contracts
// TODO(backend-contract): keep these aligned with the Django auth service.
// ============================================================================

/** POST /api/v1/register/start */
export interface StartRegistrationRequest {
  name: string;
  email: string;
  phone_no: string;
  plan_code: string;
  // Policy acceptance is stamped in the same payload as registration start.
  accept_terms: boolean;
  accept_privacy: boolean;
  accept_usage_policy: boolean;
  policy_version: string;
}

export interface StartRegistrationResponse {
  user_id: number;
  email: string;
  registration_status: RegistrationStatus;
}

/** POST /api/v1/email/verify-code */
export interface VerifyEmailCodeRequest {
  email: string;
  code: string;
}

export interface VerifyEmailCodeResponse {
  verified: true;
  user_id: number;
}

/** POST /api/v1/email/resend-code */
export interface ResendEmailCodeRequest {
  email: string;
}

export interface ResendEmailCodeResponse {
  status?: string;
  message?: string;
}

/** POST /api/v1/register/set-password */
export interface SetPasswordRegistrationRequest {
  email: string;
  password: string;
}

export interface SetPasswordRegistrationResponse {
  password_set: true;
}

/** Shared registration status shape returned across registration endpoints. */
export interface RegistrationStatus {
  is_verified: number;
  step_1_password: number;
  step_2_mfa: number;
  step_3_clinic: number;
  step_4_subscription: number;
  step_5_medical_officers: number;
  current_step: number | null;
  is_registration_complete: boolean;
  completion_percentage: number;
  user_id: number;
  user_email: string;
  clinic_id?: number;
  clinic_is_approved?: boolean;
  approval_status?: 'pending' | 'approved' | 'rejected';
}

/** POST /api/v1/registration/organization-setup/ (Bearer) */
export interface OrgClinicPayload {
  name: string;
  phone: string;
  email: string;
  abn: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zip_code: string;
  country: string;
}

export interface OrgMedicalOfficerPayload {
  name: string;
  email: string;
  phone_no: string;
  medical_officer_registration_number: string;
  specialty?: DoctorSpecialty;
}

export interface SetupOrgRequest {
  account_type: 'individual' | 'clinic';
  specialty: DoctorSpecialty;
  clinic: OrgClinicPayload | null;
  medical_officers: OrgMedicalOfficerPayload[];
  /** Clinic admin also practices as a doctor (occupies a billable seat). */
  registrant_practices?: boolean;
}

export interface SetupOrgResponse {
  registration_status: RegistrationStatus;
  clinic_id: number | null;
  next_step: 'trial' | string;
  /**
   * Re-minted OAuth2 token issued once the clinic exists. The MFA-setup token was
   * baked before the clinic was created (clinic_id=null), so the FE must swap to
   * this one for clinic-admin claims to take effect on the first session.
   */
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

/** POST /api/v1/auth/google/ */
export interface GoogleLoginRequest {
  id_token: string;
  client_id: string;
}

export interface GoogleLoginMfaRequired {
  mfa_required: true;
  mfa_tx_id: string;
  expires_in: number;
}

export interface GoogleLoginResumeRegistration {
  outcome: 'resume_registration';
  email: string;
  mfa_setup_token: string;
  registration_status: RegistrationStatus;
  current_step: number | null;
}

export interface GoogleLoginSuccess {
  data: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
    user_context: {
      id: number;
      email: string;
      phone_no: string;
      name: string;
      specialty?: string;
    specialties?: string[];
      is_admin: boolean;
      is_super_admin?: boolean;
      is_medical_officer: boolean;
      is_patient: boolean;
      clinic_id?: number;
      clinic_name?: string;
      redirect_url?: string;
      entitlements?: Entitlements;
    };
  };
}

export type GoogleLoginResponse =
  | GoogleLoginMfaRequired
  | GoogleLoginResumeRegistration
  | GoogleLoginSuccess;

export const isGoogleMfaRequired = (r: GoogleLoginResponse): r is GoogleLoginMfaRequired =>
  'mfa_required' in r && r.mfa_required === true;

export const isGoogleResumeRegistration = (
  r: GoogleLoginResponse
): r is GoogleLoginResumeRegistration => 'outcome' in r && r.outcome === 'resume_registration';

export const isGoogleLoginSuccess = (r: GoogleLoginResponse): r is GoogleLoginSuccess =>
  'data' in r && Boolean((r as GoogleLoginSuccess).data?.access_token);

/** POST /api/v1/auth/handoff/issue/ (Bearer) */
export interface HandoffTokenResponse {
  token: string;
  expires_in: number;
}

// Helper function to check if response is MFA required
export const isMFARequired = (response: LoginResponse): response is MFARequiredResponse => {
  return 'mfa_required' in response && response.mfa_required === true;
};

// Helper function to check if response is MFA setup required
export const isMFASetupRequired = (response: LoginResponse): response is MFASetupRequiredResponse => {
  return 'error' in response && response.error === 'mfa_setup_required';
};

// Removed isSuccessfulLogin - all users must go through MFA verification or setup
// Removed legacy access token based MFA setup interfaces

class AuthService extends BaseApiService {
  protected loginUrl: string;

  constructor() {
    super();
    this.loginUrl = environment.LOGIN_API_URL;
  }

  async login(loginData: LoginRequest): Promise<LoginResponse> {
    // Posts the password grant to the login endpoint, which returns an MFA challenge
    // the caller verifies at /mfa/login/verify-otp/.
    const requestBody = {
      grant_type: 'password',
      username: loginData.username,
      password: loginData.password,
      client_id: environment.CLIENT_ID,
    };

    const response = await fetch(`${this.loginUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    const loginResponse: LoginResponse = await response.json();
    return loginResponse;
  }

  async register(registerData: RegisterRequest): Promise<RegisterResponse> {
    const response = await fetch(`${this.authUrl}/register/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(registerData),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async refreshToken(): Promise<TokenResponse> {
    const refreshToken = storage.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    
    const requestBody = {
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: environment.CLIENT_ID,
    };

    const response = await fetch(`${environment.LOGIN_API_URL}/refreshtoken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async logout(): Promise<void> {
    // Clear all stored authentication and session data
    storage.clearAll();
  }

  async getUserStatus(email: string): Promise<UserStatusResponse> {
    const response = await fetch(`${this.authUrl}/registration/status/?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async verifyEmail(request: EmailVerifyRequest): Promise<EmailVerifyResponse> {
    const response = await fetch(`${this.authUrl}/email/verify/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async setPasswordWithToken(request: SetPasswordRequest): Promise<SetPasswordResponse> {
    const response = await fetch(`${this.authUrl}/password/set-with-token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async sendMFAToken(request: SendMFATokenRequest): Promise<SendMFATokenResponse> {
    const response = await fetch(`${this.authUrl}/mfa/token-verification/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async completeMFASetup(request: CompleteMFARequest): Promise<CompleteMFAResponse> {
    const response = await fetch(`${this.authUrl}/mfa/setup-complete/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async resetPassword(request: PasswordResetRequest): Promise<PasswordResetResponse> {
    const response = await fetch(`${this.authUrl}/password/reset/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async resetPasswordConfirm(request: PasswordResetConfirmRequest): Promise<PasswordResetConfirmResponse> {
    const response = await fetch(`${this.authUrl}/password/reset/confirm/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // Password Reset OTP Methods
  async sendPasswordResetOTP(request: SendPasswordResetOTPRequest): Promise<SendPasswordResetOTPResponse> {
    const response = await fetch(`${this.authUrl}/password/reset/send-otp/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async verifyPasswordResetOTP(request: VerifyPasswordResetOTPRequest): Promise<VerifyPasswordResetOTPResponse> {
    const response = await fetch(`${this.authUrl}/password/reset/verify-otp/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // Admin Registration Methods
  async setupClinic(request: ClinicSetupRequest): Promise<ClinicSetupResponse> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.authSecUrl}/registration/clinic-setup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async setupSubscription(request: SubscriptionSetupRequest): Promise<SubscriptionSetupResponse> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.authSecUrl}/registration/subscription-setup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async setupMedicalOfficers(request: MedicalOfficersSetupRequest): Promise<MedicalOfficersSetupResponse> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.authSecUrl}/medical-officers/bulk-create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // New MFA Verification Method
  async verifyMFALogin(request: MFAVerifyLoginRequest): Promise<MFAVerifyLoginResponse> {
    const response = await fetch(`${this.authUrl}/mfa/login/verify-otp/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // Legacy methods kept for compatibility
  async verifyLoginOTP(request: MFALoginVerifyRequest): Promise<MFALoginVerifyResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Get token if available
    const token = storage.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.authUrl}/mfa/login/verify-otp/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async resendLoginOTP(request: MFALoginResendRequest): Promise<MFALoginResendResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Get token if available
    const token = storage.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.authUrl}/mfa/login/resend-otp/`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // New MFA Setup Methods for Login Flow
  async initiateMFASetupForLogin(request: InitiateMFASetupLoginRequest): Promise<InitiateMFASetupLoginResponse> {
    const response = await fetch(`${this.authUrl}/mfa/access-token/verification/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async selectClinic(
    clinicId: number,
    options?: { acting_as_doctor_id?: number },
  ): Promise<{
    access_token: string;
    token_type: string;
    refresh_token: string;
    user_context: {
      id: number;
      email: string;
      name: string;
      phone_no: string;
      is_admin: boolean;
      is_super_admin: boolean;
      is_medical_officer: boolean;
      is_patient: boolean;
      clinic_id: number;
      clinic_name: string;
      acting_as_doctor_id?: number;
      acting_as_doctor_name?: string;
    };
  }> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const body: Record<string, unknown> = { clinic_id: clinicId };
    if (options?.acting_as_doctor_id) {
      body.acting_as_doctor_id = options.acting_as_doctor_id;
    }

    const response = await fetch(`${this.authSecUrl}/clinics/select/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  async completeMFASetupForLogin(request: CompleteMFASetupLoginRequest): Promise<CompleteMFASetupLoginResponse> {
    const response = await fetch(`${this.authUrl}/mfa/access-token/setup-complete/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  // ==========================================================================
  // Redesigned registration + login flow
  // TODO(backend-contract): endpoints below must match the Django auth service.
  // ==========================================================================

  /** TODO(backend-contract): POST /api/v1/register/start — starts registration
   * and stamps policy acceptance in the same payload. */
  async startRegistration(request: StartRegistrationRequest): Promise<StartRegistrationResponse> {
    const response = await fetch(`${this.authUrl}/register/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return unwrapData<StartRegistrationResponse>(await response.json());
  }

  /** TODO(backend-contract): POST /api/v1/email/verify-code */
  async verifyEmailCode(request: VerifyEmailCodeRequest): Promise<VerifyEmailCodeResponse> {
    const response = await fetch(`${this.authUrl}/email/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return unwrapData<VerifyEmailCodeResponse>(await response.json());
  }

  /** TODO(backend-contract): POST /api/v1/email/resend-code */
  async resendEmailCode(request: ResendEmailCodeRequest): Promise<ResendEmailCodeResponse> {
    const response = await fetch(`${this.authUrl}/email/resend-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  /** TODO(backend-contract): POST /api/v1/register/set-password */
  async setPassword(request: SetPasswordRegistrationRequest): Promise<SetPasswordRegistrationResponse> {
    const response = await fetch(`${this.authUrl}/register/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return unwrapData<SetPasswordRegistrationResponse>(await response.json());
  }

  /** TODO(backend-contract): POST /api/v1/registration/organization-setup/ (Bearer) */
  async setupOrg(request: SetupOrgRequest): Promise<SetupOrgResponse> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.authUrl}/registration/organization-setup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return unwrapData<SetupOrgResponse>(await response.json());
  }

  /** TODO(backend-contract): POST /api/v1/auth/google/ — GIS id_token sign-in. */
  async loginWithGoogle(idToken: string): Promise<GoogleLoginResponse> {
    const response = await fetch(`${this.authUrl}/auth/google/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_token: idToken,
        client_id: environment.CLIENT_ID,
      } as GoogleLoginRequest),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

  /** TODO(backend-contract): POST /api/v1/auth/handoff/issue/ (Bearer) — issues a
   * short-lived cross-app handoff token for GP frontend redirect. */
  async issueHandoffToken(): Promise<HandoffTokenResponse> {
    const token = storage.getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.authUrl}/auth/handoff/issue/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throwNormalizedError(response, errorBody);
    }

    return response.json();
  }

}

export const authService = new AuthService(); 